import { css } from "lit";

/** Only optional variants are styled here. The established layout keeps its original styles. */
export const variantStyles = css`
  :host([layout="v1"]), :host([layout="v2"]), :host([layout="v3"]) {
    max-width: var(--schulte-roi-max-width, 1280px);
    container: calculator / inline-size;
  }

  .calculator-layout {
    display: grid;
    grid-template-areas: "inputs" "result" "contact";
    gap: 16px;
  }
  .calculator-layout .card,
  .calculator-layout .result {
    margin: 0;
    padding: 24px;
    min-width: 0;
  }
  .calculator-layout .inputs {
    grid-area: inputs;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 32px;
  }
  .calculator-layout fieldset { margin: 0; }
  .calculator-layout legend { font-size: 0.95rem; margin-bottom: 8px; }
  .calculator-layout .question-number { width: 24px; height: 24px; margin-right: 8px; }
  .calculator-layout .slider-wrap {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0 12px;
    padding: 0;
    align-items: center;
  }
  .calculator-layout .slider-value {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    align-items: baseline;
    gap: 5px;
    margin: 0;
    padding: 8px 10px;
    border: 1px solid var(--_border);
    border-radius: 8px;
    background: var(--_surface-elevated);
    font-size: 1.1rem;
  }
  .calculator-layout .value-input { width: 4.6ch; border: 0; padding: 0; }
  .calculator-layout .slider-value small { display: inline; margin: 0; font-size: 0.8rem; }
  .calculator-layout input[type="range"] { grid-column: 1; grid-row: 1; }
  .calculator-layout .slider-ticks { grid-column: 1; font-size: 0.75rem; }
  .calculator-layout .options { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
  .calculator-layout .option-btn { padding: 12px 8px; font-size: 0.9rem; }

  .calculator-layout .result {
    grid-area: result;
    container: result / inline-size;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
    align-content: start;
    gap: 12px;
    text-align: left;
  }
  .calculator-layout .result-label,
  .calculator-layout .result-main,
  .calculator-layout .result-meta,
  .calculator-layout .result-disclaimer { grid-column: 1 / -1; margin: 0; }
  .calculator-layout .result-label { font-size: 0.8rem; }
  .calculator-layout .result-main { font-size: 1.05rem; }
  .calculator-layout .result-main strong { font-size: 1.75rem; margin: 3px 0 0; }
  .calculator-layout .result-box { margin: 0; padding: 12px; font-size: 0.85rem; }
  .calculator-layout .result-box.roi { font-size: 0.9rem; }
  .calculator-layout .result-box.roi .amount { font-size: 1.8rem; }
  .calculator-layout .result-secondary {
    display: block;
    max-width: 100%;
    width: fit-content;
    font-size: 0.8rem;
    padding: 4px 8px;
  }
  .calculator-layout .result-meta { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .calculator-layout .result-meta-item { padding: 8px 10px; font-size: 0.75rem; }
  .calculator-layout .result-meta-item strong { font-size: 0.9rem; }
  .calculator-layout .result-disclaimer { font-size: 0.72rem; }

  .calculator-layout .form-section { grid-area: contact; container: contact / inline-size; }
  .calculator-layout .form-section h2 { font-size: 1.25rem; text-align: left; margin-bottom: 6px; }
  .calculator-layout .form-section .subtitle { text-align: left; margin-bottom: 14px; font-size: 0.9rem; }
  .calculator-layout form { display: grid; gap: 14px; grid-template-columns: minmax(0, 1fr); }
  .calculator-layout .form-row { margin: 0; min-width: 0; }
  .calculator-layout .form-row input { min-width: 0; padding: 10px 12px; font-size: 0.95rem; }
  .calculator-layout .form-row label { font-size: 0.85rem; }
  .calculator-layout .required-hint,
  .calculator-layout .privacy,
  .calculator-layout .consent,
  .calculator-layout .submit-btn,
  .calculator-layout form > [role="status"] { grid-column: 1 / -1; margin: 0; }
  .calculator-layout .privacy { font-size: 0.75rem; }
  .calculator-layout .submit-btn { padding: 14px; font-size: 1rem; }

  .request-summary { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 18px; font-size: 0.8rem; }
  .request-summary .request-label { flex-basis: 100%; color: var(--_text-muted); }
  .request-summary > span:not(.request-label) { background: var(--_surface-elevated); border-radius: 20px; padding: 4px 8px; font-weight: 600; }
  .request-summary small { font-size: 0.75rem; font-weight: 400; color: var(--_text-muted); }
  .request-summary .request-cap { color: var(--_accent); background: var(--_accent-soft) !important; }
  .calculator-layout > .cap-notice { grid-column: 1 / -1; margin: 0; }
  .calculator-layout .cap-notice { font-size: 0.85rem; }
  .calculator-layout .cap-notice details { margin-top: 6px; }
  .calculator-layout .cap-notice summary { cursor: pointer; font-weight: 600; }

  @container contact (min-width: 340px) {
    .calculator-layout form { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .calculator-layout .form-row:nth-child(n + 3) { grid-column: 1 / -1; }
  }
  @container contact (min-width: 520px) {
    .calculator-layout .form-row:nth-child(n + 3) { grid-column: auto; }
  }

  @container calculator (min-width: 860px) {
    .calculator-layout[data-layout="v1"] {
      grid-template-columns: minmax(0, 1.2fr) minmax(320px, 1fr);
      grid-template-areas: "inputs contact" "result contact";
      align-items: start;
    }
    .calculator-layout[data-layout="v1"] .form-section { position: sticky; top: 24px; }
  }

  .calculator-layout[data-layout="v2"] .result { grid-template-columns: minmax(0, 1fr); }
  .calculator-layout[data-layout="v2"] .result-main strong { font-size: min(1.5rem, 8cqi); white-space: nowrap; }
  @container calculator (min-width: 640px) {
    .calculator-layout[data-layout="v2"] {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-areas: "inputs result" "contact contact";
    }
  }
  @container calculator (min-width: 1040px) {
    .calculator-layout[data-layout="v2"] {
      grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 1.15fr);
      grid-template-areas: "inputs result contact";
    }
  }

  .calculator-layout[data-layout="v3"] .request-label { flex-basis: auto; }
  .calculator-layout[data-layout="v3"] .request-summary > span:not(.request-label) { padding: 0; background: none; }
  @container calculator (min-width: 600px) {
    .calculator-layout[data-layout="v3"] .inputs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 28px; }
    .calculator-layout[data-layout="v3"] fieldset:last-child { grid-column: 1 / -1; }
  }
  @container calculator (min-width: 820px) {
    .calculator-layout[data-layout="v3"] {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-areas: "inputs inputs" "result contact";
    }
  }
  @container calculator (min-width: 960px) {
    .calculator-layout[data-layout="v3"] .inputs { grid-template-columns: repeat(3, minmax(0, 1fr)); align-items: start; }
    .calculator-layout[data-layout="v3"] fieldset:last-child { grid-column: auto; }
  }
`;
